import { FilterExpression } from '@infuse/api/common';
import { ServerLogEntryType } from '@infuse/api/cpms/config';
import { FilterCollection } from '@infuse/cpms/charging-station/filters';
import { Filter, FilterConfiguration, FilterConfigurationType, FilterOption } from '@infuse/ui/domain';
import dayjs from 'dayjs';
import { computed, onMounted, reactive, Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import {
  MessageType,
  Ocpp1xMessageAction,
  Ocpp201MessageAction,
  Ocpp21MessageAction,
  OcppMessageAction,
  Protocol,
  ServerLog
} from './model';
import { useServerLogStore } from './useLoggingStore';

export type MessageTypeFilterOption = {
  name: ServerLogEntryType;
  label: () => string;
  toUmbrellaExpression: () => FilterExpression;
};

export type MessageTypeFilter = {
  name: 'messageType';
  type: FilterConfigurationType;
  label: () => string;
  options: MessageTypeFilterOption[];
};

export type ActionTypeFilterOption = {
  name: string;
  label: () => string;
  toUmbrellaExpression: () => FilterExpression;
};

export type ActionTypeFilter = {
  name: 'actionType';
  type: FilterConfigurationType;
  label: () => string;
  options: ActionTypeFilterOption[];
};

const createOptions = (actions: string[]) => {
  return Object.values(actions).map((ma) => ({
    name: ma,
    label: () => ma,
    toUmbrellaExpression(): FilterExpression {
      return new FilterExpression().equals('procUri', ma);
    }
  }));
};

type TranslateFn = ReturnType<typeof useI18n>['t'];

const createMessageTypeFilter = (t: TranslateFn): MessageTypeFilter => {
  return {
    name: 'messageType',
    type: FilterConfigurationType.SELECT,
    label: () => t('chargingStation.serverLog.filters.messageType.label'),
    options: Object.values(ServerLogEntryType)
      .filter((et) => et !== ServerLogEntryType.REJECTED_TOKEN && et !== ServerLogEntryType.VALID_TOKEN) // don't show token filters as they are always filtered out in the store
      .map((et) => ({
        name: et,
        label: () => t(`chargingStation.serverLog.filters.messageType.${et}`),
        toUmbrellaExpression(): FilterExpression {
          return new FilterExpression().equals('messageType', et, true);
        }
      }))
  };
};

const createActionTypeFilter = (t: TranslateFn): ActionTypeFilter => {
  return {
    name: 'actionType',
    type: FilterConfigurationType.SELECT,
    label: () => t('chargingStation.serverLog.filters.actionType.label'),
    options: createOptions(Object.values(OcppMessageAction))
  };
};

const twoWeeksAgo = dayjs().subtract(2, 'weeks').startOf('day').toDate();

export function useServerLog(chargingStationId: Ref<string>, protocol: Ref<Protocol>) {
  const { t } = useI18n({ useScope: 'global' });
  const store = useServerLogStore();
  const router = useRouter();
  const messageTypeFilter = createMessageTypeFilter(t);
  const actionTypeFilter = createActionTypeFilter(t);

  const route = useRoute();
  const startDateTime = ref<Date>(twoWeeksAgo);
  const endDateTime = ref<Date | undefined>();
  const filterQuery = ref<string | undefined>(route.query.filter?.toString());

  // The initial filter configuration
  const filterConfigs = ref<FilterConfiguration[]>([messageTypeFilter, actionTypeFilter]);

  // Create the initial filter collection. Make it reactive such that we can watch filter changes.
  const filterCollection = reactive(new FilterCollection());
  const filters = computed(() => filterCollection.filters);
  if (filterQuery?.value !== undefined) {
    // Add the filters contained in the url query part
    filterCollection.decodeAndAddFilters(filterQuery.value, filterConfigs.value, true);
  }

  const requestServerLog = ref<ServerLog>();
  const responseServerLog = ref<ServerLog>();

  const addFilter = (filter: Filter): void => {
    filterCollection.add(filter);
  };

  const addFilterByActionTypeName = (args: {
    actionTypeName: string;
    negated?: boolean;
    searchQuery?: string;
  }): void => {
    const filterOption: FilterOption | undefined = actionTypeFilter.options.find(
      (f: ActionTypeFilterOption) => f.name === args.actionTypeName
    );

    if (!filterOption) {
      throw new Error(`Unable to find ActionTypeFilterOption with name [${args.actionTypeName}].
      Ensure that 'actionTypeFilter.options' contains the specified actionType name`);
    }

    addFilter({
      config: {
        // FilterConfiguration
        type: actionTypeFilter.type,
        label: actionTypeFilter.label,
        name: actionTypeFilter.name,
        options: actionTypeFilter.options
      },
      selectedOption: filterOption, // FilterOption
      negated: args.negated ?? false,
      searchQuery: args.searchQuery ?? '' // Required to filter out the existing one, overcomes duplicate when already in Url.
    });
  };

  const removeFilter = (filter: Filter): void => {
    filterCollection.remove(filter);
  };

  const removeAllFilters = (): void => {
    filterCollection.clear();
  };

  const reset = (): void => {
    startDateTime.value = twoWeeksAgo;
    endDateTime.value = undefined;
    removeAllFilters();
  };

  const onDateRangeChange = (date: Date | Date[]): void => {
    if (Array.isArray(date)) {
      startDateTime.value = date[0];
      endDateTime.value = date[1];
    } else {
      startDateTime.value = date;
    }
  };

  const onSelected = (row: ServerLog): void => {
    if (row.messageType === MessageType.REQUEST) {
      requestServerLog.value = row;
      responseServerLog.value = store.getResponse(row.callId, row.logDate);
    } else if (row.messageType === MessageType.RESPONSE || row.messageType === MessageType.ERROR) {
      requestServerLog.value = store.getRequest(row.callId, row.logDate);
      responseServerLog.value = row;
    } else if (row.messageType === MessageType.FIRE_AND_FORGET) {
      requestServerLog.value = row;
      responseServerLog.value = undefined;
    }
  };

  const refresh = async (): Promise<void> => {
    updateFiltersOnProtocolChange();
    replaceQueryParamsInUrl();
    await updateStoreFilter();
  };

  const updateFiltersOnFilterChange = async (): Promise<void> => {
    replaceQueryParamsInUrl();
    await updateStoreFilter();
  };

  const updateFiltersOnProtocolChange = (): void => {
    // Set the action filter values to whatever the CS supports.
    switch (protocol.value) {
      case Protocol.OCPPJ15:
      case Protocol.OCPPJ16:
        actionTypeFilter.options = createOptions(Object.values(Ocpp1xMessageAction));
        break;
      case Protocol.OCPP201:
        actionTypeFilter.options = createOptions(Object.values(Ocpp201MessageAction));
        break;
      case Protocol.OCPP21:
        actionTypeFilter.options = createOptions(Object.values(Ocpp21MessageAction));
        break;
      case Protocol.UNKNOWN:
      default:
        actionTypeFilter.options = createOptions(Object.values(OcppMessageAction));
        break;
    }
  };

  const updateFiltersOnDateRangeChange = async (): Promise<void> => {
    replaceQueryParamsInUrl();
    await updateStoreFilter();
  };

  const updateStoreFilter = async (): Promise<void> => {
    if (chargingStationId.value === undefined) {
      return;
    }

    await store.setFilter(
      chargingStationId.value,
      filterCollection.toFilterExpression(),
      startDateTime.value,
      endDateTime.value
    );
  };

  const replaceQueryParamsInUrl = (): void => {
    const input = {
      name: router.currentRoute.value.name?.toString(),
      query: {
        filter: filterCollection.urlEncode() || undefined,
        start: `${startDateTime.value.getTime()}` || undefined,
        ...(endDateTime.value != null && { end: `${endDateTime?.value?.getTime()}` })
      }
    };

    if (!endDateTime.value) {
      delete input.query.end;
    }

    void router.replace(input);
  };

  function parseTimestamp<F extends Date | undefined | null>(
    raw: string | string[] | undefined,
    fallback: F
  ): Date | F {
    if (typeof raw !== 'string') {
      return fallback;
    }
    const ms = Number(raw);
    return Number.isFinite(ms) ? new Date(ms) : fallback;
  }

  onMounted(async (): Promise<void> => {
    await updateStoreFilter(); // ensure a reload occurs of the entries after navigating back.
  });

  watch(protocol, () => updateFiltersOnProtocolChange(), { immediate: true });

  watch([startDateTime, endDateTime], () => updateFiltersOnDateRangeChange());

  watch(
    () => filterCollection.filters,
    () => {
      void updateFiltersOnFilterChange();
    },
    { deep: true }
  );

  watch(
    chargingStationId,
    (chargingStationId) => {
      if (chargingStationId) {
        reset();
      }
    },
    { immediate: false }
  );

  watch(
    () => route.query?.start?.toString(),
    (newStart) => {
      startDateTime.value = parseTimestamp(newStart, twoWeeksAgo);
    },
    { immediate: true }
  );

  watch(
    () => route.query?.end?.toString(),
    (newEnd) => {
      endDateTime.value = parseTimestamp(newEnd, undefined);
    },
    { immediate: true }
  );

  return {
    startDateTime,
    endDateTime,
    filters,
    filterConfigs,
    actionTypeFilter,
    requestServerLog,
    responseServerLog,
    addFilter,
    addFilterByActionTypeName,
    removeFilter,
    onDateRangeChange,
    refresh,
    onSelected
  };
}
